// src/app/components/reporting/types/index.ts

export interface SavedInsight {
  id:           number;
  username:     string;
  brand_name:   string;
  brand_run_id?: number | null;
  module:       string;
  title:        string;
  subtitle?:    string | null;
  metric?:      string | null;
  module_color: string;
  saved_from?:  string | null;
  raw_data:     Record<string, unknown>;
  created_at:   string;
}

export interface InsightCreatePayload {
  brand_name:   string;
  brand_run_id?: number | null;
  module:       string;
  title:        string;
  subtitle?:    string;
  metric?:      string;
  module_color?: string;
  saved_from?:  string;
  raw_data?:    Record<string, unknown>;
}

export interface ReportSection {
  title:              string;
  content:            string;
  key_metric?:        string | null;
  source_insight_ids: number[];
  reasoning:          string;
}

export interface GeneratedReport {
  id:                number;
  brand_name:        string;
  prompt:            string;
  title:             string;
  audience:          string;
  format:            string;
  confidence:        number;
  executive_summary: string;
  sections:          ReportSection[];
  key_findings:      string[];
  recommendations:   string[];
  insight_count:     number;
  created_at:        string;
}

export interface ReportGenerateRequest {
  brand_name:    string;
  prompt:        string;
  insight_ids?:  number[];
  brand_run_id?: number | null;
  audience?:     string;
  format?:       string;
}

/* ── module identity ────────────────────────────────────────────────────────

   `MODULE_COLORS` was here: a module name mapped to ONE `var(--x)` string,
   which every tinted surface in this module then tried to give an alpha by
   concatenating hex digits onto:

       backgroundColor: module_color + '18'    // "var(--signal-blue)18"

   You cannot append alpha to a `var()` reference. The declaration is invalid,
   the browser drops it silently, and every tinted pill in Insights & Reports
   rendered untinted from the day it shipped.

   It is DELETED rather than corrected, because a map that returns one colour
   invites the same arithmetic again. `module-identity.ts` returns a triple —
   colour, tint, edge — all three from the token file, and there is nothing
   left to concatenate. */

/* ── audience and format ────────────────────────────────────────────────────

   Derived from QUICK_STARTS rather than listed a second time. The composer's
   pill rows and the quick-start cards have to offer the SAME set: a pill row
   holding a format no quick start uses, or a quick start setting a format the
   pills cannot show, is the kind of near-miss that survives review because
   both halves look right on their own.

   Declared after QUICK_STARTS below, which is why they are at the end of this
   file. */

export const EXPORT_FORMATS = [
  { id: 'powerpoint', iconName: 'FileText', label: 'PowerPoint',     description: 'Download .pptx slides',       available: true },
  { id: 'email',      iconName: 'Mail',     label: 'Email summary',   description: 'Copy and open in email client', available: true },
  { id: 'link',       iconName: 'Link2',    label: 'Shareable link',  description: 'Copy public URL to clipboard',  available: true },
  { id: 'linkedin',   iconName: 'Linkedin', label: 'LinkedIn post',   description: 'Formatted and ready to paste',  available: true },
];

export const QUICK_STARTS = [
  {
    id: 'qs-1',
    prompt: 'Show brand perception improvements with the metrics that back them up.',
    iconName: 'Zap', color: 'var(--a-info-t)',
    format: 'Sales Deck', audience: 'Technical Buyer',
  },
  {
    id: 'qs-2',
    prompt: 'Brief the board on where we stand against competitors and what changes next.',
    iconName: 'TrendingUp', color: 'var(--a-reason-t)',
    format: 'Executive Summary', audience: 'C-Suite / Board',
  },
  {
    id: 'qs-3',
    prompt: 'Demonstrate ROI and momentum since the engagement began.',
    iconName: 'Users', color: 'var(--a-brand-t)',
    format: 'Client Briefing', audience: 'Client / Customer',
  },
];

/**
 * The audience and format values, in the spelling the API stores.
 *
 * Stored Title Case, DISPLAYED sentence case through `format.ts`. The values
 * themselves are frozen: they go out on `ReportGenerateRequest` and come back
 * on `GeneratedReport`, and rows carrying this spelling already exist.
 */
export const AUDIENCES: readonly string[] = Array.from(
  new Set(QUICK_STARTS.map((q) => q.audience)),
);

export const FORMATS: readonly string[] = Array.from(
  new Set(QUICK_STARTS.map((q) => q.format)),
);
